// Edit-mode helper for benchmark targets. Wraps the upsert + target-update APIs
// and refreshes the cached benchmark list so the UI reflects the new goal
// immediately. Used by the Overview goal cells and the Benchmarks page.
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { updateBenchmarkTarget, upsertBenchmark } from "@/lib/api/reference";
import type { ActivityType } from "@/lib/api/types";

export interface BenchmarkKey {
  metric_key: string;
  activity_type: ActivityType;
  region: string | null;
  brand_id: string | null;
}

export function useBenchmarkEditor() {
  const qc = useQueryClient();
  const refresh = useCallback(() => qc.invalidateQueries({ queryKey: ["ref", "benchmarks"] }), [qc]);

  /** Update an existing benchmark by id. */
  const saveById = useCallback(
    async (id: string, value: number) => {
      await updateBenchmarkTarget(id, value);
      await refresh();
    },
    [refresh],
  );

  /** Create-or-update by natural key (used when a goal cell has no stored row). */
  const saveByKey = useCallback(
    async (key: BenchmarkKey, value: number) => {
      await upsertBenchmark({ ...key, target_value: value });
      await refresh();
    },
    [refresh],
  );

  return { saveById, saveByKey };
}
